import React, { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    TablePagination,
    TableSortLabel,
} from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Styles from "./DailyReportTable.module.scss";

// Sort Helpers
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const DailyReportTable = ({ data, onRowClick }) => {
    // Helper to convert HH:MM to minutes
    const timeToMinutes = (timeStr) => {
        if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":")) return 0;
        const [h, m] = timeStr.split(":").map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    // Helper to convert minutes to HH:MM
    const minutesToTime = (mins) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    };

    // State for Pagination and Sorting
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("projectName");

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // 1. Flatten Data (for sorting and pagination)
    const flattenedData = useMemo(() => {
        if (!data || data.length === 0) return [];

        const hierarchy = {};
        const projectTotals = {};
        const issueCounts = {}; // Track count of tasks per issue type

        // First pass: Calculate totals and counts
        data.forEach((item) => {
            const pName = item.projectName || "Unknown Project";
            const iType = item.issueType || "Other";
            const duration = timeToMinutes(item.totalHours);

            if (!hierarchy[pName]) {
                hierarchy[pName] = {};
                projectTotals[pName] = 0;
                issueCounts[pName] = {};
            }
            if (!hierarchy[pName][iType]) {
                hierarchy[pName][iType] = 0;
                issueCounts[pName][iType] = 0;
            }

            hierarchy[pName][iType] += duration;
            projectTotals[pName] += duration;
            issueCounts[pName][iType] += 1; // Count each task
        });

        const rows = [];
        const projects = Object.keys(hierarchy).sort();

        projects.forEach((pName) => {
            const issueTypes = Object.keys(hierarchy[pName]).sort();
            const projectTotalTime = minutesToTime(projectTotals[pName]);
            const projectTotalMins = projectTotals[pName]; // for sorting

            issueTypes.forEach((iType) => {
                rows.push({
                    projectName: pName,
                    issueType: iType,
                    taskCount: issueCounts[pName][iType], // Add count
                    formattedIssueByHours: minutesToTime(hierarchy[pName][iType]),
                    issueByHoursValue: hierarchy[pName][iType], // for sorting
                    formattedProjectTotalHours: projectTotalTime,
                    projectTotalValue: projectTotalMins, // for sorting
                });
            });
        });

        return rows;
    }, [data]);

    // 2. Sort Data
    const sortedData = useMemo(() => {
        const comparator = (a, b) => {
            const multiplier = order === 'asc' ? 1 : -1;

            switch (orderBy) {
                case 'projectName':
                    // Sort Projects
                    return a.projectName.localeCompare(b.projectName) * multiplier;

                case 'projectTotalValue':
                    // Sort Projects by Total Value
                    if (a.projectTotalValue !== b.projectTotalValue) {
                        return (a.projectTotalValue - b.projectTotalValue) * multiplier;
                    }
                    return a.projectName.localeCompare(b.projectName) * multiplier;

                case 'issueType':
                    // Sort Issue Types WITHIN Project
                    // First ensure projects are grouped/sorted (default name sort)
                    if (a.projectName !== b.projectName) {
                        return a.projectName.localeCompare(b.projectName);
                    }
                    // Then sort Issue Type
                    return a.issueType.localeCompare(b.issueType) * multiplier;

                case 'issueByHoursValue':
                    // Sort Issue Types by Hour Value WITHIN Project
                    if (a.projectName !== b.projectName) {
                        return a.projectName.localeCompare(b.projectName);
                    }
                    return (a.issueByHoursValue - b.issueByHoursValue) * multiplier;

                default:
                    return 0;
            }
        };

        return [...flattenedData].sort(comparator);

    }, [flattenedData, order, orderBy]);

    // 3. Paginate
    const visibleRows = useMemo(() => {
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    // 4. Calculate Spans for Visible Page
    const processedPageRows = useMemo(() => {
        const rows = [];
        let i = 0;
        while (i < visibleRows.length) {
            const current = visibleRows[i];
            const currentProject = current.projectName;

            // Calculate Project Span (number of consecutive rows with same project on THIS page)
            let projectSpan = 1;
            for (let j = i + 1; j < visibleRows.length; j++) {
                if (visibleRows[j].projectName === currentProject) {
                    projectSpan++;
                } else {
                    break;
                }
            }

            // Push rows
            for (let k = 0; k < projectSpan; k++) {
                rows.push({
                    ...visibleRows[i + k],
                    projectRowSpan: k === 0 ? projectSpan : 0
                });
            }
            i += projectSpan;
        }
        return rows;
    }, [visibleRows]);


    const headerCells = [
        { id: 'projectName', label: 'Project Name', sortable: true },
        { id: 'issueType', label: 'Issue Type', sortable: true },
        { id: 'issueByHoursValue', label: 'Working Hours', sortable: true }, // Sort by value not string
        { id: 'projectTotalValue', label: 'Total Hours', sortable: true }, // Sort by value
    ];

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    return (
        <Box>
            <div className={Styles.renderingTable}>
                <TableContainer className={Styles.tableContainer}>
                    <Box className={Styles.tableContent}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {headerCells.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            className={`${Styles.TableCell} ${headCell.id === 'projectName' ? Styles.ProjectCell : ''}`}
                                            // ProjectCell here doesn't have border due to SCSS change (TableBodyCell.ProjectCell), perfect.
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            {headCell.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === headCell.id}
                                                    direction={orderBy === headCell.id ? order : 'asc'}
                                                    onClick={createSortHandler(headCell.id)}
                                                >
                                                    {headCell.label}
                                                    {orderBy === headCell.id ? (
                                                        <Box component="span" sx={{ ...Styles.visuallyHidden, display: 'none' }}>
                                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : headCell.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedPageRows.length > 0 ? (
                                    processedPageRows.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{ "&:hover": { backgroundColor: "#f0f8ff !important" } }}
                                        >
                                            {/* Project Name Cell - Spanned */}
                                            {row.projectRowSpan > 0 && (
                                                <TableCell
                                                    className={`${Styles.TableBodyCell} ${Styles.SpannedCell} ${Styles.ProjectCell}`}
                                                    rowSpan={row.projectRowSpan}
                                                    sx={{ fontWeight: "600 !important" }}
                                                >
                                                    {row.projectName}
                                                </TableCell>
                                            )}

                                            {/* Issue Type Cell with Count */}
                                            <TableCell
                                                className={Styles.TableBodyCell}
                                            >
                                                {row.issueType} <span style={{ color: '#008080', fontWeight: 600 }}>({row.taskCount})</span>
                                            </TableCell>

                                            {/* Issue Type Working Hours Cell */}
                                            <TableCell
                                                className={Styles.TableBodyCell}
                                                onClick={() => onRowClick && onRowClick(row.projectName, row.issueType)}
                                                sx={{
                                                    cursor: "pointer",
                                                    padding: "8px !important"
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#008080",
                                                        fontWeight: "600",
                                                        fontSize: "14px",
                                                        cursor: "pointer",
                                                        transition: "color 0.2s ease",
                                                        "&:hover": {
                                                            textDecoration: "underline",
                                                            color: "#005555"
                                                        }
                                                    }}
                                                >
                                                    {row.formattedIssueByHours || "00:00"}
                                                    <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                                </Box>
                                            </TableCell>

                                            {/* Project Total Hours Cell - Spanned and Clickable */}
                                            {row.projectRowSpan > 0 && (
                                                <TableCell
                                                    className={`${Styles.TableBodyCell} ${Styles.SpannedCell} ${Styles.TotalCell}`}
                                                    rowSpan={row.projectRowSpan}
                                                    onClick={() => onRowClick && onRowClick(row.projectName, null)}
                                                    sx={{
                                                        fontWeight: "bold !important",
                                                        color: "#008080 !important",
                                                        borderLeft: "1px solid #d5d7e8 !important",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            textDecoration: "underline",
                                                            color: "#005555 !important"
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}
                                                    >
                                                        {row.formattedProjectTotalHours}
                                                        <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{ borderBottom: "none", padding: "20px" }}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                flexDirection="column"
                                            >
                                                <Typography color="textSecondary">
                                                    No Data Available
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    <Box
                        sx={{
                            borderTop: "1px solid #e0e0e0",
                            bgcolor: "#fafafa",
                            display: "flex",
                            justifyContent: "flex-end",
                            width: "100%",
                            overflowX: "hidden"
                        }}
                    >
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={flattenedData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                border: "none",
                                "& .MuiTablePagination-toolbar": {
                                    minHeight: "52px",
                                    paddingLeft: { xs: "8px", md: "16px" },
                                    paddingRight: { xs: "8px", md: "16px" },
                                    flexWrap: "wrap"
                                },
                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                    fontSize: "14px",
                                    color: "#666",
                                    margin: 0
                                },
                                "& .MuiTablePagination-select": {
                                    fontSize: "14px",
                                    borderRadius: "6px",
                                    padding: "4px 8px"
                                },
                                "& .MuiTablePagination-actions": {
                                    marginLeft: { xs: "4px", md: "20px" }
                                },
                                "& .MuiIconButton-root": {
                                    color: "#008080",
                                    padding: { xs: "4px", md: "8px" },
                                    "&:hover": {
                                        bgcolor: "rgba(0, 128, 128, 0.08)"
                                    },
                                    "&.Mui-disabled": {
                                        color: "#ccc"
                                    }
                                }
                            }}
                        />
                    </Box>
                </TableContainer>
            </div>
        </Box >
    );
}

export default DailyReportTable;
